import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { CardGeneric } from 'components/CardGeneric';
import { Txt } from 'components/Txt';
import { Card } from 'components/Card';
import { DateField } from 'components/Field/DateField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { formatDate } from 'lib/riskbase';
import { API_DATE_FORMAT, UI_DATE_FORMAT_SHORT } from 'lib/constants';
import { parseISO } from 'date-fns';
import { doAseApprove, NAVIGATE_MODAL_BACK } from 'store/actions';
import { useSelector } from 'hooks/useSelector';
import {
  selectAseActionTimescaleDate,
  selectAseActionTimescaleUpdateReviewed,
} from 'store/assessments/selectors';
import { useEsc } from 'hooks/useEsc';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { CardRow } from 'components/CardRow';

export const StatusApproveModal: React.FC<
  ModalScreenProps<ModalScreens.AseStatusApprove>
> = ({ navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const rawActionTimescaleUpdateReviewed = useSelector(
    selectAseActionTimescaleUpdateReviewed,
  );
  const rawActionTimescaleDate = useSelector(selectAseActionTimescaleDate);
  const [actionTimescaleDate, setActionTimescaleDate] = React.useState(
    rawActionTimescaleDate || formatDate(new Date(), API_DATE_FORMAT),
  );
  const [actionTimescaleUpdateReviewed, setActionTimescaleUpdateReviewed] =
    React.useState(rawActionTimescaleUpdateReviewed);

  const [note, setNote] = React.useState('');

  const handleClose = () => {
    if (note) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = () =>
    dispatch(
      doAseApprove({
        actionTimescaleUpdateReviewed,
        actionTimescaleDate,
        note,
      }),
    );

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !note });
  }, [navigation, note]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
          saveButtonText="Approve"
          heading="Assessment"
        />
      }
    >
      <CardGeneric>
        <Txt>
          Changes cannot be made once this assessment is approved. Tasks will be
          created for actions identified in the assessment
        </Txt>
      </CardGeneric>

      <SeparatorHorizontal withMarginBottom />
      <Card
        explanation={`Actions due "within 1 month" will be due "${formatDate(
          parseISO(actionTimescaleDate),
          UI_DATE_FORMAT_SHORT,
        )} + 1 month"`}
      >
        <DateField
          label="Action Timescale Date"
          onChange={setActionTimescaleDate}
          value={actionTimescaleDate}
          clearable={false}
        />
      </Card>

      <Card
        explanation={
          actionTimescaleUpdateReviewed
            ? 'Reviewed actions will use the new action timescale date'
            : 'Reviewed actions will keep their existing timescale dates'
        }
      >
        <CardRow
          label="Update Reviewed Actions' Timescales"
          onSwitchChange={() =>
            setActionTimescaleUpdateReviewed(!actionTimescaleUpdateReviewed)
          }
          switchValue={actionTimescaleUpdateReviewed}
        />
      </Card>

      <Card explanation="Your note will be displayed in the assessment's history">
        <TextAreaField
          placeholder="Optional Note"
          onChange={setNote}
          value={note}
          onEsc={handleClose}
        />
      </Card>
    </PageScrollView>
  );
};
